import { useConfigurableAction } from "@shesha-io/reactjs";
import { DOCUMENT_ACTIONS, DOCUMENT_ACTIONS_OWNER } from "../configurable-actions/model";


interface FileContext {
    fileId: string;
}

interface Contexts {
    currentFile?: FileContext;
}

interface User {
    id: string;
}

interface Application {
    user: User;
}

interface HttpResponse<T> {
    data: {
        result: T;
    };
}

interface Http {
    post<T = unknown>(url: string): Promise<HttpResponse<T>>;
}

interface ActionContext {
    contexts?: Contexts;
    http: Http;
    application: Application;
}

interface CloseViewerMessage {
    type: 'CLOSE_VIEWER';
}

/**
 * We pass `IConfigurableActionArguments` as TArguments and `void` as TResponse
 * so the `executer` signature satisfies `IRegisterActionPayload` without
 * conflicting with its internal declaration of the `executer` callback.
 * `context` is typed structurally via `ActionContext` — cast it if the
 * framework's own context type differs at the call-site.
 */
export const useDocumentViewerAction = () => {
    useConfigurableAction({
        owner: DOCUMENT_ACTIONS_OWNER,
        ownerUid: DOCUMENT_ACTIONS_OWNER,
        name: DOCUMENT_ACTIONS.VIEW_AND_SIGN,
        hasArguments: true,
        executer: async (_actionArgs, context) => {
            const { contexts, http, application } = context as unknown as ActionContext;

            // Get the document ID from file context
            const documentId = contexts?.currentFile?.fileId;

            if (!documentId) {
                console.error("No document ID found in file context");
                throw new Error("No document available for viewing");
            }

            const url = `/api/services/Memos/Nutrient/InitiateDigitalSigning?storedFileId=${documentId}`;

            try {
                const response = await http.post<string>(url);

                const { user } = application;
                const viewerDocumentId = response.data.result;
                const viewerUrl = `/pspdf-viewer/${encodeURIComponent(viewerDocumentId)}?userId=${encodeURIComponent(user.id)}`;

                // Create modal overlay
                const overlay = document.createElement('div');
                overlay.id = 'document-viewer-overlay';
                overlay.style.cssText = `
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.45);
                    z-index: 1000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `;

                // Create modal container WITHOUT header
                const modal = document.createElement('div');
                modal.id = 'document-viewer-modal';
                modal.style.cssText = `
                    position: relative;
                    width: 100%;
                    height: 100%;
                    max-width: 100vw;
                    max-height: 100vh;
                    background: white;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                `;

                // Create iframe container (no header, full height)
                const iframeContainer = document.createElement('div');
                iframeContainer.style.cssText = `
                    flex: 1;
                    position: relative;
                    overflow: hidden;
                    height: 100%;
                `;

                // Create loading indicator
                const loadingDiv = document.createElement('div');
                loadingDiv.style.cssText = `
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 16px;
                    color: rgba(0, 0, 0, 0.65);
                    z-index: 10;
                `;
                loadingDiv.textContent = 'Loading document...';

                // Create iframe
                const iframe = document.createElement('iframe');
                iframe.src = viewerUrl;
                iframe.style.cssText = `
                    width: 100%;
                    height: 100%;
                    border: none;
                    display: none;
                `;

                // Close function
                const closeModal = (): void => {
                    // Guard against double-close — bail only when the overlay
                    // has already been removed.
                    if (!document.getElementById('document-viewer-overlay')) {
                        return;
                    }
                    document.body.style.overflow = '';
                    overlay.remove();
                    document.removeEventListener('keydown', handleEscape);
                    window.removeEventListener('message', handleMessage);
                };

                // Listen for close message from iframe
                const handleMessage = (event: MessageEvent<string | CloseViewerMessage>): void => {
                    try {
                        const isCloseString = event.data === 'CLOSE_VIEWER';
                        const isCloseObject =
                            typeof event.data === 'object' &&
                            event.data !== null &&
                            event.data.type === 'CLOSE_VIEWER';

                        if (isCloseString || isCloseObject) {
                            console.log('Received close message from viewer');
                            closeModal();
                        }
                    } catch (err) {
                        console.log('Message parsing error:', err);
                    }
                };

                window.addEventListener('message', handleMessage);

                // Show iframe when loaded
                iframe.onload = (): void => {
                    loadingDiv.style.display = 'none';
                    iframe.style.display = 'block';

                    // Send a message to the iframe to set up the close handler
                    setTimeout(() => {
                        try {
                            iframe.contentWindow?.postMessage(
                                { type: 'SETUP_CLOSE_HANDLER' },
                                '*'
                            );
                        } catch (err) {
                            console.log('Could not send setup message:', err);
                        }
                    }, 1000);
                };

                // Close on overlay click (outside modal)
                overlay.onclick = (e: MouseEvent): void => {
                    if (e.target === overlay) {
                        closeModal();
                    }
                };

                // Close on ESC key
                const handleEscape = (e: KeyboardEvent): void => {
                    if (e.key === 'Escape') {
                        closeModal();
                    }
                };

                document.addEventListener('keydown', handleEscape);

                // Assemble the modal (NO HEADER)
                iframeContainer.appendChild(loadingDiv);
                iframeContainer.appendChild(iframe);
                modal.appendChild(iframeContainer);
                overlay.appendChild(modal);

                // Add to document
                document.body.appendChild(overlay);

                // Prevent body scroll when modal is open
                document.body.style.overflow = 'hidden';
            } catch (error) {
                console.error("Error initiating digital signing:", error);
                throw error;
            }
        }
    }, []);
};
